import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Check as ApproveIcon,
  RestartAlt as ReopenIcon,
  DoneAll as CompleteIcon,
  Download as DownloadIcon,
  FilterAltOff as ClearFiltersIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import { ledgerAPI } from '../../services/api';
import { normalizeBalances, normalizeRecon, normalizeRecons } from '../../utils/ledgerIds';
import { formatAccountingAmount, downloadCsv } from '../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import ModulePageHeader from '../common/ModulePageHeader';
import { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import { AccountPicker, CurrencyPicker, FiscalPeriodPicker, FiscalYearPicker, useLedgerOptions } from './LedgerPickers';

const getErrorMessage = (error, fallback = 'Operation failed') =>
  error?.response?.data?.message || fallback;

const unwrap = (response) => response?.data?.data ?? response?.data ?? {};
const unwrapRawList = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : payload.items ?? [];
};
// Both account-balances and subledger-recons return the same
// { items, total, page, limit } shape once paginated server-side; a bare
// array (old/unpaginated caller) is normalized the same way.
const unwrapPaged = (response) => {
  const payload = unwrap(response);
  if (Array.isArray(payload)) return { items: payload, total: payload.length, page: 1, perPage: payload.length || 20 };
  return {
    items: payload.items ?? [],
    total: Number(payload.total ?? (payload.items || []).length ?? 0),
    page: Number(payload.page ?? 1),
    perPage: Number(payload.limit ?? 20),
  };
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const amt = (v) => formatAccountingAmount(v);

const isZero = (v) => Math.abs(num(v)) < 0.005;

// Mirrors accounting-apis SubledgerReconType / SubledgerReconStatus enums
// exactly (prisma/schema/subledger-recon.prisma) - the UI must never offer a
// value the ledger's IsEnum() validator will reject.
const RECON_TYPES = ['INVENTORY', 'TAX', 'PAYROLL', 'SUSPENSE'];
const RECON_TYPE_INFO = {
  INVENTORY: 'Ties GL inventory control accounts to the item costing subledger.',
  TAX: 'Ties GL tax payable/receivable accounts to open tax returns.',
  PAYROLL: 'Ties GL payroll control accounts to payroll runs and outstanding advances.',
  SUSPENSE: 'Manual schedule against a GL clearing/suspense account you choose.',
};
const RECON_STATUSES = ['DRAFT', 'APPROVED', 'COMPLETED'];

const BALANCE_SORT_OPTIONS = [
  { value: 'balance_id', label: 'Default order' },
  { value: 'closing_balance', label: 'Closing balance' },
  { value: 'net_movement', label: 'Net movement' },
  { value: 'period_debit', label: 'Period debit' },
  { value: 'created_at', label: 'Recently created' },
];

// Sort/enum single-select built on the same Autocomplete pattern used across
// the ledger screens (see Journals.js EntriesTab filter bar).
const SelectFilter = ({ label, value, onChange, options, sx }) => {
  const withAll = [{ label: `All ${label.toLowerCase()}`, value: '' }, ...options];
  return (
    <Autocomplete
      size="small"
      options={withAll}
      getOptionLabel={(o) => o.label}
      value={withAll.find((o) => o.value === value) || withAll[0]}
      onChange={(_, o) => onChange(o?.value || '')}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      renderInput={(params) => <TextField {...params} label={label} />}
      sx={sx}
    />
  );
};

// Row actions as a "⋮" dropdown, same pattern as Journals.js's row menus:
// a bare icon button per action reads as visual noise once a table has any
// destructive action, and doesn't scale as more actions apply per row.
// Renders nothing when there are no items (e.g. the viewer can't delete).
const RowActionsMenu = ({ items = [] }) => {
  const [anchor, setAnchor] = useState(null);
  if (!items.length) return null;
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ elevation: 2, sx: { minWidth: 190 } }}
      >
        {items.map((it) => (
          <MenuItem key={it.label} onClick={() => { close(); it.onClick(); }} sx={it.danger ? { color: 'error.main' } : undefined}>
            <ListItemIcon>{it.icon}</ListItemIcon>
            {it.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ReconStatusChip = ({ status }) => {
  const st = (status || 'DRAFT').toUpperCase();
  const cfg = { DRAFT: 'warning', APPROVED: 'info', COMPLETED: 'success' }[st] || 'default';
  return <Chip label={st} color={cfg} size="small" variant="outlined" />;
};

const StatTile = ({ label, value, color }) => (
  <Box sx={{ flex: '1 1 140px', textAlign: 'center', py: 1.5, px: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={700} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

const StatStrip = ({ tiles }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    divider={<Divider orientation="vertical" flexItem />}
    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}
  >
    {tiles.map((t) => (
      <StatTile key={t.label} {...t} />
    ))}
  </Stack>
);

// Period labels for subledger recons: period_id on that model is a bare int
// by design (see subledger-recon.prisma) - no embedded relation, so it's
// resolved client-side from the same cached fiscal-period list the other
// ledger screens already share (Journals.js uses the same cache key).
const usePeriodLookup = () => {
  const { options } = useLedgerOptions('lookup-periods', () =>
    ledgerAPI.getFiscalPeriods({ limit: 100 }).then(unwrapRawList)
  );
  return useMemo(() => {
    const map = new Map();
    (options || []).forEach((p) => {
      const id = p.period_id ?? p.id;
      if (id != null) map.set(String(id), p);
    });
    return (id) => (id ? map.get(String(id))?.period_name || `Period #${id}` : '-');
  }, [options]);
};

// ---------------------------------------------------------------------------
// Balances tab - period balance snapshots
// ---------------------------------------------------------------------------

const EMPTY_BALANCE_FILTERS = {
  search: '',
  account_id: '',
  period_id: '',
  fiscal_year_id: '',
  foreign_currency_id: '',
  has_foreign_currency: '',
  min_closing_balance: '',
  max_closing_balance: '',
};

const BalancesTab = ({ can, showSnackbar, confirm }) => {
  const resource = RESOURCES.LEDGER.GL;
  const canUpdate = can(resource, ACTIONS.UPDATE);
  const canDelete = can(resource, ACTIONS.DELETE);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [filters, setFilters] = useState(EMPTY_BALANCE_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('balance_id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [initDlg, setInitDlg] = useState({ open: false, period_id: '' });

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) => v !== '' && v !== null && v !== undefined),
    [filters]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((p) => (p.search === searchInput ? p : { ...p, search: searchInput }));
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [filters, sortBy, sortOrder]);

  const load = useCallback(async () => {
    try {
      if (items.length === 0) setLoading(true);
      else setFetching(true);
      const params = { page, limit: size, sortBy, sortOrder };
      if (filters.search) params.search = filters.search;
      if (filters.account_id) params.account_id = Number(filters.account_id);
      if (filters.period_id) params.period_id = Number(filters.period_id);
      if (filters.fiscal_year_id) params.fiscal_year_id = Number(filters.fiscal_year_id);
      if (filters.foreign_currency_id) params.foreign_currency_id = Number(filters.foreign_currency_id);
      if (filters.has_foreign_currency) params.has_foreign_currency = filters.has_foreign_currency === 'true';
      if (filters.min_closing_balance !== '') params.min_closing_balance = Number(filters.min_closing_balance);
      if (filters.max_closing_balance !== '') params.max_closing_balance = Number(filters.max_closing_balance);
      const res = await ledgerAPI.getAccountBalances(params);
      const { items: raw, total: t } = unwrapPaged(res);
      setItems(normalizeBalances(raw));
      setTotal(t);
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch balances'), 'error');
    } finally {
      setLoading(false);
      setFetching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, sortBy, sortOrder, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key) => (e) => setFilters((p) => ({ ...p, [key]: e.target.value }));
  const clearFilters = () => {
    setSearchInput('');
    setFilters(EMPTY_BALANCE_FILTERS);
  };

  const askDelete = (b) => {
    confirm({
      title: 'Delete balance',
      message: "Delete this balance row? It won't touch journal entries, but if a later period's opening balance was carried forward from this one, that account will open at zero next time you initialize. Prefer \"Recalculate period\" to fix a wrong number - only delete to remove a duplicate/bad row.",
      severity: 'error',
      onConfirm: async () => {
        try {
          await ledgerAPI.deleteAccountBalance(b.id);
          showSnackbar('Balance deleted successfully', 'success');
          load();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const submitInitialize = async () => {
    if (!initDlg.period_id) {
      showSnackbar('Period is required', 'error');
      return;
    }
    try {
      await ledgerAPI.initializeBalances({ period_id: Number(initDlg.period_id) });
      showSnackbar('Zero balances initialized for all active accounts', 'success');
      setInitDlg({ open: false, period_id: '' });
      load();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const recalculate = () => {
    const pid = filters.period_id || initDlg.period_id;
    if (!pid) {
      showSnackbar('Choose a period first (filter or initialize dialog)', 'error');
      return;
    }
    confirm({
      title: 'Recalculate balances',
      message: `Rebuild every balance in this period from posted lines?`,
      onConfirm: async () => {
        try {
          await ledgerAPI.recalculateBalances(Number(pid));
          showSnackbar('Balances recalculated successfully', 'success');
          load();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const loadPeriodSummary = async () => {
    if (!filters.period_id) {
      showSnackbar('Choose a period to summarize', 'error');
      return;
    }
    try {
      const res = await ledgerAPI.getBalanceSummaryByPeriod(Number(filters.period_id));
      setSummary({ kind: 'period', data: unwrap(res) });
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch period summary'), 'error');
    }
  };

  const loadAccountSummary = async () => {
    if (!filters.account_id) {
      showSnackbar('Choose an account to summarize', 'error');
      return;
    }
    try {
      const res = await ledgerAPI.getBalanceSummaryByAccount(Number(filters.account_id));
      setSummary({ kind: 'account', data: unwrap(res) });
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch account summary'), 'error');
    }
  };

  const exportCsv = () => {
    const headers = [
      'Account Code', 'Account Name', 'Period', 'Opening Debit', 'Opening Credit',
      'Period Debit', 'Period Credit', 'Closing Debit', 'Closing Credit',
      'Net Movement', 'Closing Balance', 'FX Currency',
    ];
    const rows = items.map((b) => [
      b.account?.account_code || '',
      b.account?.account_name || '',
      b.period?.period_name || b.period_id || '',
      num(b.opening_debit), num(b.opening_credit),
      num(b.period_debit), num(b.period_credit),
      num(b.closing_debit), num(b.closing_credit),
      num(b.net_movement), num(b.closing_balance),
      b.foreign_currency?.currency_code || '',
    ]);
    downloadCsv('account-balances.csv', headers, rows);
  };

  const toggleSortOrder = () => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={2}>
            {canUpdate && (
              <>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setInitDlg({ open: true, period_id: filters.period_id })}>
                  Initialize period
                </Button>
                <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={recalculate}>
                  Recalculate period
                </Button>
              </>
            )}
            <Button size="small" variant="outlined" onClick={loadPeriodSummary}>Period summary</Button>
            <Button size="small" variant="outlined" onClick={loadAccountSummary}>Account summary</Button>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!items.length}>
              Export CSV
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small" label="Search"
              placeholder="Account code, name, period..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Box sx={{ minWidth: 240 }}>
              <AccountPicker value={filters.account_id} onChange={(id) => setFilters((p) => ({ ...p, account_id: id }))} />
            </Box>
            <Box sx={{ minWidth: 200 }}>
              <FiscalYearPicker value={filters.fiscal_year_id} onChange={(id) => setFilters((p) => ({ ...p, fiscal_year_id: id }))} />
            </Box>
            <Box sx={{ minWidth: 220 }}>
              <FiscalPeriodPicker value={filters.period_id} onChange={(id) => setFilters((p) => ({ ...p, period_id: id }))} yearId={filters.fiscal_year_id || undefined} />
            </Box>
            <Box sx={{ minWidth: 220 }}>
              <CurrencyPicker label="FX currency" value={filters.foreign_currency_id} onChange={(id) => setFilters((p) => ({ ...p, foreign_currency_id: id }))} />
            </Box>
            <SelectFilter
              label="FX rows"
              value={filters.has_foreign_currency}
              onChange={(v) => setFilters((p) => ({ ...p, has_foreign_currency: v }))}
              options={[{ value: 'true', label: 'FX only' }, { value: 'false', label: 'Local currency only' }]}
              sx={{ minWidth: 190 }}
            />
            <TextField size="small" type="number" label="Min closing" value={filters.min_closing_balance} onChange={setFilter('min_closing_balance')} sx={{ width: 140 }} />
            <TextField size="small" type="number" label="Max closing" value={filters.max_closing_balance} onChange={setFilter('max_closing_balance')} sx={{ width: 140 }} />
            <SelectFilter
              label="Sort by"
              value={sortBy}
              onChange={(v) => setSortBy(v || 'balance_id')}
              options={BALANCE_SORT_OPTIONS.filter((o) => o.value !== 'balance_id')}
              sx={{ minWidth: 200 }}
            />
            <Tooltip title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}>
              <IconButton onClick={toggleSortOrder}>
                {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
              </IconButton>
            </Tooltip>
            {hasActiveFilters && (
              <Button variant="outlined" size="small" startIcon={<ClearFiltersIcon />} onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </Box>

          {summary?.kind === 'period' && (() => {
            const s = summary.data || {};
            const balanced = isZero(num(s.total_closing_debit) - num(s.total_closing_credit));
            return (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Period summary - {s.period_name || `#${s.period_id}`}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      icon={balanced ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                      label={balanced ? 'Closing balances tie' : 'Closing debit ≠ closing credit'}
                      color={balanced ? 'success' : 'error'}
                      size="small"
                    />
                    <Button size="small" onClick={() => setSummary(null)}>Close</Button>
                  </Stack>
                </Stack>
                <StatStrip
                  tiles={[
                    { label: 'Opening Debit', value: amt(s.total_opening_debit) },
                    { label: 'Opening Credit', value: amt(s.total_opening_credit) },
                    { label: 'Period Debit', value: amt(s.total_period_debit) },
                    { label: 'Period Credit', value: amt(s.total_period_credit) },
                    { label: 'Closing Debit', value: amt(s.total_closing_debit) },
                    { label: 'Closing Credit', value: amt(s.total_closing_credit) },
                    { label: 'Accounts', value: s.account_count ?? 0 },
                  ]}
                />
              </Box>
            );
          })()}

          {summary?.kind === 'account' && (() => {
            const s = summary.data || {};
            return (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Account summary - {s.account_code} {s.account_name}</Typography>
                  <Button size="small" onClick={() => setSummary(null)}>Close</Button>
                </Stack>
                <StatStrip
                  tiles={[
                    { label: 'Total Debit', value: amt(s.total_debit) },
                    { label: 'Total Credit', value: amt(s.total_credit) },
                    { label: 'Net Balance', value: amt(s.net_balance), color: num(s.net_balance) < 0 ? 'error.main' : undefined },
                    { label: 'Periods', value: s.period_count ?? 0 },
                  ]}
                />
              </Box>
            );
          })()}
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {fetching && <LinearProgress />}
          <Box sx={{ opacity: fetching ? 0.5 : 1, transition: 'opacity 0.2s', p: 2 }}>
            {items.length === 0 && !loading ? (
              <EmptyRecordsState message="No balances found. Initialize the period to create zero rows, or post journals first." />
            ) : (
              <DataTable
                columns={['Account', 'Period', 'Opening Bal', 'Period Dr', 'Period Cr', 'Net Movement', 'Closing Bal', 'FX', 'Actions']}
                data={items}
                loading={loading}
                emptyMessage="No balances found."
                minWidth={1050}
                renderRow={(b) => (
                  <TableRow key={b.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{b.account?.account_code || b.account_id}</Typography>
                      <Typography variant="caption" color="text.secondary">{b.account?.account_name || ''}</Typography>
                    </TableCell>
                    <TableCell>{b.period?.period_name || b.period_id}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{amt(num(b.opening_debit) - num(b.opening_credit))}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{amt(b.period_debit)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{amt(b.period_credit)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: num(b.net_movement) < 0 ? 'error.main' : undefined }}>
                      {amt(b.net_movement)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      <strong>{amt(b.closing_balance ?? num(b.closing_debit) - num(b.closing_credit))}</strong>
                    </TableCell>
                    <TableCell>
                      {b.foreign_currency_id ? (
                        <Chip size="small" label={b.foreign_currency?.currency_code || `#${b.foreign_currency_id}`} variant="outlined" />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <RowActionsMenu
                        items={canDelete ? [{
                          label: 'Delete (OPEN periods only)',
                          icon: <DeleteIcon fontSize="small" color="error" />,
                          danger: true,
                          onClick: () => askDelete(b),
                        }] : []}
                      />
                    </TableCell>
                  </TableRow>
                )}
              />
            )}
            <TablePagination
              pagination={{ current_page: page, last_page: Math.max(1, Math.ceil(total / size)), per_page: size, total }}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(s) => { setSize(parseInt(s, 10)); setPage(1); }}
              isMobile={false}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Balances are snapshots rebuilt from POSTED/APPROVED lines on close and recalculation -
              never edit them to fix the books, post a journal instead.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={initDlg.open} onClose={() => setInitDlg({ open: false, period_id: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Initialize period balances</DialogTitle>
        <DialogContent>
          <FiscalPeriodPicker
            label="Period"
            value={initDlg.period_id}
            onChange={(id) => setInitDlg((p) => ({ ...p, period_id: id }))}
            sx={{ mt: 1 }}
            helper="Creates zero rows for all active accounts. Fails if already initialized."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInitDlg({ open: false, period_id: '' })}>Cancel</Button>
          <Button variant="contained" onClick={submitInitialize}>Initialize</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Subledger recon tab - GL to subledger tie-out
// ---------------------------------------------------------------------------

const EMPTY_RECON_FILTERS = {
  search: '',
  recon_type: '',
  status: '',
  period_id: '',
  account_id: '',
  date_from: '',
  date_to: '',
};

const emptyCreateDlg = () => ({ open: false, recon_type: 'INVENTORY', period_id: '', account_id: '', as_of_date: '', notes: '' });

const ReconTab = ({ can, showSnackbar, confirm }) => {
  const resource = RESOURCES.LEDGER.GL;
  const canCreate = can(resource, ACTIONS.CREATE);
  const canUpdate = can(resource, ACTIONS.UPDATE);
  const canDelete = can(resource, ACTIONS.DELETE);
  const canApprove = can(resource, ACTIONS.APPROVE);
  const periodLabel = usePeriodLookup();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [filters, setFilters] = useState(EMPTY_RECON_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [createDlg, setCreateDlg] = useState(emptyCreateDlg());
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [lineDlg, setLineDlg] = useState({ open: false, kind: 'manual', label: '', amount: '', explanation: '' });

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) => v !== '' && v !== null && v !== undefined),
    [filters]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((p) => (p.search === searchInput ? p : { ...p, search: searchInput }));
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const load = useCallback(async () => {
    try {
      if (items.length === 0) setLoading(true);
      else setFetching(true);
      const params = { page, limit: size };
      if (filters.search) params.search = filters.search;
      if (filters.recon_type) params.recon_type = filters.recon_type;
      if (filters.status) params.status = filters.status;
      if (filters.period_id) params.period_id = Number(filters.period_id);
      if (filters.account_id) params.account_id = Number(filters.account_id);
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await ledgerAPI.getSubledgerRecons(params);
      const { items: raw, total: t } = unwrapPaged(res);
      setItems(normalizeRecons(raw));
      setTotal(t);
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch reconciliations'), 'error');
    } finally {
      setLoading(false);
      setFetching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key) => (e) => setFilters((p) => ({ ...p, [key]: e.target.value }));
  const clearFilters = () => {
    setSearchInput('');
    setFilters(EMPTY_RECON_FILTERS);
  };

  const openDetail = async (row) => {
    setDetail(row);
    setDetailLoading(true);
    try {
      const res = await ledgerAPI.getSubledgerRecon(row.id);
      const data = normalizeRecon(unwrap(res));
      setDetail({ ...row, ...data, id: row.id });
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch reconciliation'), 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const submitCreate = async () => {
    if (createDlg.recon_type === 'SUSPENSE' && !createDlg.account_id) {
      showSnackbar('Select the clearing account for a SUSPENSE reconciliation', 'error');
      return;
    }
    const body = { recon_type: createDlg.recon_type };
    if (createDlg.period_id) body.period_id = Number(createDlg.period_id);
    if (createDlg.recon_type === 'SUSPENSE' && createDlg.account_id) body.account_id = Number(createDlg.account_id);
    if (createDlg.as_of_date) body.as_of_date = createDlg.as_of_date;
    if (createDlg.notes.trim()) body.notes = createDlg.notes.trim();
    try {
      await ledgerAPI.createSubledgerRecon(body);
      showSnackbar('Reconciliation prepared with a fresh snapshot', 'success');
      setCreateDlg(emptyCreateDlg());
      load();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitLine = async () => {
    if (!lineDlg.label.trim() || !lineDlg.amount || !lineDlg.explanation.trim()) {
      showSnackbar('Label, amount and explanation are required', 'error');
      return;
    }
    try {
      if (lineDlg.kind === 'manual') {
        await ledgerAPI.addManualReconLine(detail.id, {
          label: lineDlg.label.trim(),
          adjustment_amount: Number(lineDlg.amount),
          explanation: lineDlg.explanation.trim(),
        });
      } else {
        await ledgerAPI.addScheduleReconLine(detail.id, {
          label: lineDlg.label.trim(),
          amount: Number(lineDlg.amount),
          explanation: lineDlg.explanation.trim(),
        });
      }
      showSnackbar('Line added successfully', 'success');
      setLineDlg({ open: false, kind: 'manual', label: '', amount: '', explanation: '' });
      openDetail(detail);
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const runAction = (label, fn, success) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this reconciliation?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(success, 'success');
          setDetail(null);
          load();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const exportCsv = () => {
    const headers = ['Recon #', 'Type', 'Status', 'Period', 'As-of Date', 'GL Total', 'Subledger Total', 'Difference', 'Explained', 'Unexplained'];
    const rows = items.map((r) => [
      r.recon_number || r.id,
      r.recon_type || '',
      r.status || '',
      periodLabel(r.period_id),
      r.as_of_date ? String(r.as_of_date).slice(0, 10) : '',
      num(r.gl_total), num(r.subledger_total), num(r.difference), num(r.explained_total), num(r.unexplained),
    ]);
    downloadCsv('subledger-reconciliations.csv', headers, rows);
  };

  const lines = detail?.lines ?? [];
  const isDraft = (detail?.status || '').toUpperCase() === 'DRAFT';
  const isApproved = (detail?.status || '').toUpperCase() === 'APPROVED';
  const detailTied = detail ? isZero(detail.unexplained) : false;

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={2}>
            {canCreate && (
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreateDlg((p) => ({ ...p, open: true }))}>
                New Reconciliation
              </Button>
            )}
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!items.length}>
              Export CSV
            </Button>
            <Typography variant="caption" color="text.secondary">
              Tie out GL control accounts against their subledgers - approve requires explanation coverage, complete requires zero unexplained difference.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small" label="Search"
              placeholder="Recon number, notes..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <SelectFilter
              label="Type" value={filters.recon_type}
              onChange={(v) => setFilters((p) => ({ ...p, recon_type: v }))}
              options={RECON_TYPES.map((t) => ({ value: t, label: t }))}
              sx={{ minWidth: 170 }}
            />
            <SelectFilter
              label="Status" value={filters.status}
              onChange={(v) => setFilters((p) => ({ ...p, status: v }))}
              options={RECON_STATUSES.map((s) => ({ value: s, label: s }))}
              sx={{ minWidth: 170 }}
            />
            <Box sx={{ minWidth: 220 }}>
              <FiscalPeriodPicker value={filters.period_id} onChange={(id) => setFilters((p) => ({ ...p, period_id: id }))} />
            </Box>
            <Box sx={{ minWidth: 260 }}>
              <AccountPicker label="Account (SUSPENSE)" value={filters.account_id} onChange={(id) => setFilters((p) => ({ ...p, account_id: id }))} />
            </Box>
            <TextField size="small" type="date" label="As-of from" value={filters.date_from} onChange={setFilter('date_from')} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
            <TextField size="small" type="date" label="As-of to" value={filters.date_to} onChange={setFilter('date_to')} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
            {hasActiveFilters && (
              <Button variant="outlined" size="small" startIcon={<ClearFiltersIcon />} onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {fetching && <LinearProgress />}
          <Box sx={{ opacity: fetching ? 0.5 : 1, transition: 'opacity 0.2s', p: 2 }}>
            {items.length === 0 && !loading ? (
              <EmptyRecordsState message="No subledger reconciliations found." />
            ) : (
              <DataTable
                columns={['Recon #', 'Type', 'Period', 'As-of', 'GL Total', 'Subledger Total', 'Unexplained', 'Status', 'Actions']}
                data={items}
                loading={loading}
                emptyMessage="No subledger reconciliations found."
                minWidth={1050}
                renderRow={(r) => {
                  const tied = isZero(r.unexplained);
                  return (
                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(r)}>
                      <TableCell><strong>{r.recon_number || `#${r.id}`}</strong></TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.recon_type || '-'}</Typography>
                        {r.account && (
                          <Typography variant="caption" color="text.secondary">
                            {r.account.account_code} - {r.account.account_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{periodLabel(r.period_id)}</TableCell>
                      <TableCell>{r.as_of_date ? String(r.as_of_date).slice(0, 10) : '-'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{amt(r.gl_total)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{amt(r.subledger_total)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: tied ? 'success.main' : 'error.main', fontWeight: 700 }}>
                        {amt(r.unexplained)}
                      </TableCell>
                      <TableCell><ReconStatusChip status={r.status} /></TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <RowActionsMenu
                          items={canDelete && (r.status || '').toUpperCase() === 'DRAFT' ? [{
                            label: 'Delete draft',
                            icon: <DeleteIcon fontSize="small" color="error" />,
                            danger: true,
                            onClick: () => runAction('Delete reconciliation', () => ledgerAPI.deleteSubledgerRecon(r.id), 'Deleted successfully'),
                          }] : []}
                        />
                      </TableCell>
                    </TableRow>
                  );
                }}
              />
            )}
            <TablePagination
              pagination={{ current_page: page, last_page: Math.max(1, Math.ceil(total / size)), per_page: size, total }}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(s) => { setSize(parseInt(s, 10)); setPage(1); }}
              isMobile={false}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog open={createDlg.open} onClose={() => setCreateDlg((p) => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>New reconciliation</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small" select label="Type"
            value={createDlg.recon_type}
            onChange={(e) => setCreateDlg((p) => ({ ...p, recon_type: e.target.value, account_id: e.target.value === 'SUSPENSE' ? p.account_id : '' }))}
            SelectProps={{ native: true }}
            sx={{ mt: 1 }}
          >
            {RECON_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
          </TextField>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {RECON_TYPE_INFO[createDlg.recon_type]}
          </Typography>
          <FiscalPeriodPicker
            label="Period (optional - as-of end of period)"
            value={createDlg.period_id}
            onChange={(id) => setCreateDlg((p) => ({ ...p, period_id: id }))}
            sx={{ mt: 2 }}
          />
          {createDlg.recon_type === 'SUSPENSE' && (
            <AccountPicker
              label="Clearing account (required)"
              required
              value={createDlg.account_id}
              onChange={(id) => setCreateDlg((p) => ({ ...p, account_id: id }))}
              sx={{ mt: 2 }}
            />
          )}
          <TextField fullWidth size="small" type="date" label="As-of date (defaults to now, or period end)" value={createDlg.as_of_date} onChange={(e) => setCreateDlg((p) => ({ ...p, as_of_date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mt: 2 }} />
          <TextField fullWidth size="small" multiline rows={2} label="Notes" value={createDlg.notes} onChange={(e) => setCreateDlg((p) => ({ ...p, notes: e.target.value }))} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDlg((p) => ({ ...p, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate}>Prepare</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1}>
            <span>{detail?.recon_number || `#${detail?.id}`} &middot; {detail?.recon_type}</span>
            <Stack direction="row" spacing={1} alignItems="center">
              <ReconStatusChip status={detail?.status} />
              {!detailLoading && detail && (
                <Chip
                  icon={detailTied ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                  label={detailTied ? 'Tied out' : 'Unexplained difference'}
                  color={detailTied ? 'success' : 'error'}
                  size="small"
                />
              )}
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : (
            <>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Period</Typography>
                  <Typography variant="body2">{periodLabel(detail?.period_id)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">As-of date</Typography>
                  <Typography variant="body2">{detail?.as_of_date ? String(detail.as_of_date).slice(0, 10) : '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Account</Typography>
                  <Typography variant="body2">{detail?.account ? `${detail.account.account_code} - ${detail.account.account_name}` : '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Notes</Typography>
                  <Typography variant="body2">{detail?.notes || '-'}</Typography>
                </Grid>
              </Grid>

              <StatStrip
                tiles={[
                  { label: 'GL Total', value: amt(detail?.gl_total) },
                  { label: 'Subledger Total', value: amt(detail?.subledger_total) },
                  { label: 'Difference', value: amt(detail?.difference) },
                  { label: 'Explained', value: amt(detail?.explained_total) },
                  { label: 'Unexplained', value: amt(detail?.unexplained), color: detailTied ? 'success.main' : 'error.main' },
                ]}
              />

              <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                {canUpdate && isDraft && (
                  <>
                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => runAction('Refresh snapshot', () => ledgerAPI.refreshSubledgerRecon(detail.id), 'Snapshot recomputed')}>
                      Refresh
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setLineDlg({ open: true, kind: 'manual', label: '', amount: '', explanation: '' })}>
                      Add manual line
                    </Button>
                    {detail?.recon_type === 'SUSPENSE' && (
                      <Button size="small" variant="outlined" onClick={() => setLineDlg({ open: true, kind: 'schedule', label: '', amount: '', explanation: '' })}>
                        Add schedule line
                      </Button>
                    )}
                  </>
                )}
                {canApprove && isDraft && (
                  <Button size="small" variant="contained" color="success" startIcon={<ApproveIcon />} onClick={() => runAction('Approve reconciliation', () => ledgerAPI.approveSubledgerRecon(detail.id), 'Reconciliation approved')}>
                    Approve
                  </Button>
                )}
                {canApprove && isApproved && (
                  <>
                    <Button size="small" variant="outlined" startIcon={<ReopenIcon />} onClick={() => runAction('Reopen reconciliation', () => ledgerAPI.reopenSubledgerRecon(detail.id), 'Reconciliation reopened')}>
                      Reopen
                    </Button>
                    <Button size="small" variant="contained" color="success" startIcon={<CompleteIcon />} onClick={() => runAction('Complete reconciliation', () => ledgerAPI.completeSubledgerRecon(detail.id), 'Reconciliation completed')}>
                      Complete
                    </Button>
                  </>
                )}
              </Box>

              {lines.length === 0 ? (
                <Typography color="text.secondary">No lines yet.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Kind</TableCell>
                      <TableCell>Label</TableCell>
                      <TableCell align="right">GL Amount</TableCell>
                      <TableCell align="right">Subledger Amount</TableCell>
                      <TableCell align="right">Adjustment</TableCell>
                      <TableCell>Explanation</TableCell>
                      {canUpdate && isDraft && <TableCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((l, i) => {
                      const kind = (l.kind || 'MANUAL').toUpperCase();
                      const auto = kind === 'AUTO';
                      return (
                        <TableRow key={l.id ?? i}>
                          <TableCell>
                            <Chip size="small" variant="outlined" label={kind} color={auto ? 'default' : kind === 'SCHEDULE' ? 'info' : 'primary'} />
                          </TableCell>
                          <TableCell>
                            {l.label || '-'}
                            {l.reference && <Typography variant="caption" color="text.secondary" display="block">{l.reference}</Typography>}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{auto ? amt(l.gl_amount) : '-'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{auto ? amt(l.subledger_amount) : '-'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{!auto ? amt(l.adjustment_amount) : '-'}</TableCell>
                          <TableCell>{l.explanation || '-'}</TableCell>
                          {canUpdate && isDraft && (
                            <TableCell>
                              {!auto && l.id && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => runAction('Delete line', () => ledgerAPI.deleteReconLine(l.id).then(() => openDetail(detail)), 'Line deleted')}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={lineDlg.open} onClose={() => setLineDlg((p) => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>{lineDlg.kind === 'manual' ? 'Add manual line' : 'Add schedule line (SUSPENSE only)'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="Label" value={lineDlg.label} onChange={(e) => setLineDlg((p) => ({ ...p, label: e.target.value }))} sx={{ mt: 1 }} />
          <TextField fullWidth size="small" type="number" label={lineDlg.kind === 'manual' ? 'Adjustment amount (signed)' : 'Amount'} value={lineDlg.amount} onChange={(e) => setLineDlg((p) => ({ ...p, amount: e.target.value }))} sx={{ mt: 2 }} />
          <TextField fullWidth size="small" multiline rows={2} label="Explanation" value={lineDlg.explanation} onChange={(e) => setLineDlg((p) => ({ ...p, explanation: e.target.value }))} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineDlg((p) => ({ ...p, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={submitLine}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const AccountBalances = () => {
  const { can } = useAccessControl();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const [tab, setTab] = useState('balances');

  if (!can(RESOURCES.LEDGER.GL, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view account balances.</Alert>;
  }

  return (
    <Box>
      <ModulePageHeader title="Balances & Reconciliation" subtitle="Period balance snapshots plus GL to subledger tie-out" />
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab value="balances" label="Balances" />
            <Tab value="recons" label="Subledger Tie-outs" />
          </Tabs>
        </CardContent>
      </Card>
      {tab === 'balances' && <BalancesTab can={can} showSnackbar={showSnackbar} confirm={confirm} />}
      {tab === 'recons' && <ReconTab can={can} showSnackbar={showSnackbar} confirm={confirm} />}
      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
};

export default AccountBalances;
